import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Loader2, CheckCircle, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fusionUseCases = [
  { value: "machining", label: "Machining" },
  { value: "fabrication", label: "Fabrication" },
  { value: "product_design", label: "Product Design" },
  { value: "hobby", label: "Hobby / Personal" },
  { value: "other", label: "Other" },
];

const EmailCaptureModal = ({ isOpen, onClose, planType = "free", onUpgrade }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [fusionUseCase, setFusionUseCase] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const isPaidPlan = planType === "early_adopter" || planType === "standard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Track event
      // Optional: track modal interaction
      await axios.post(`${API_URL}/api/events`, {
        event_type: "modal_submitted",
        email: email,
        metadata: { plan_type: planType }
      });

      // Save lead
      const response = await axios.post(`${API_URL}/api/leads`, {
        email,
        name: name || null,
        fusion_use_case: fusionUseCase || null,
        plan_interest: planType
      });
      
      // 🔥 ADD THIS
      await axios.post(`${API_URL}/api/auth/register`, {
        email: email,
        name: name || ""
      });

      // 🔥 Track actual conversion (THIS IS THE BIG ONE)
      await axios.post(`${API_URL}/api/events`, {
        event_type: "email_submitted",
        email: email,
        metadata: { plan_type: planType }
      });

      // Store email for future use
      localStorage.setItem("paramsnap_email", email);

      if (isPaidPlan) {
        // For paid plans, redirect to Stripe checkout
        setLoading(true);
        try {
          const checkoutResponse = await axios.post(`${API_URL}/api/checkout/create-session`, {
            email: email,
            plan: planType,
            origin_url: window.location.origin
          });
          
          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.data.url;
        } catch (checkoutError) {
          const errorMsg = checkoutError.response?.data?.detail || "Failed to start checkout";
          setError(errorMsg);
          setLoading(false);
        }
      } else {
        // For free plan, trigger download
        setSuccess(true);

        const downloadLink = response.data.download_link;
        const link = document.createElement("a");
        link.href = downloadLink;
        link.download = "paramsnap.zip";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          onClose();
          setSuccess(false);
          setEmail("");
          setName("");
          setFusionUseCase("");
        }, 2000);
      }

    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof errorMsg === "string" ? errorMsg : "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const planLabels = {
    free: "Download ParamSnap Free",
    early_adopter: "Get Early Adopter ($50/yr)",
    standard: "Get Standard Plan ($100/yr)"
  };

  const buttonLabels = {
    free: "Download Free",
    early_adopter: "Continue to Payment",
    standard: "Continue to Payment"
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="email-capture-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-[#121214] border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-8 my-auto max-h-[90vh] overflow-y-auto"
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="modal-close-btn"
          >
            <X className="w-5 h-5" />
          </button>

          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-6 sm:py-8"
            >
              <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                Download Started!
              </h3>
              <p className="text-sm text-zinc-400">
                Check your email for installation instructions.
              </p>
            </motion.div>
          ) : (
            <>
              <div className="text-center mb-5 sm:mb-6 pr-8">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${isPaidPlan ? 'bg-green-500/10' : 'bg-blue-500/10'} flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
                  {isPaidPlan ? (
                    <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                  ) : (
                    <Download className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                  )}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {planLabels[planType] || "Get ParamSnap"}
                </h3>
                <p className="text-xs sm:text-sm text-zinc-400">
                  {isPaidPlan 
                    ? "Enter your email to continue to secure checkout."
                    : "Enter your email to get the download link and installation guide."}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs sm:text-sm text-zinc-300">
                    Email <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="name" className="text-xs sm:text-sm text-zinc-300">
                    Name <span className="text-zinc-500">(optional)</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label className="text-xs sm:text-sm text-zinc-300">
                    What do you use Fusion 360 for? <span className="text-zinc-500">(optional)</span>
                  </Label>
                  <Select value={fusionUseCase} onValueChange={setFusionUseCase}>
                    <SelectTrigger 
                      className="mt-1 bg-[#0A0A0B] border-white/10 text-white focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11 w-full"
                      data-testid="use-case-select"
                    >
                      <SelectValue placeholder="Select use case" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-white/10 max-w-[calc(100vw-24px)]">
                      {fusionUseCases.map((useCase) => (
                        <SelectItem 
                          key={useCase.value} 
                          value={useCase.value}
                          className="text-white hover:bg-white/5 focus:bg-white/5 text-sm"
                        >
                          {useCase.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {error && (
                  <p className="text-xs sm:text-sm text-red-400 text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className={`w-full py-2.5 sm:py-3 rounded-lg font-medium flex items-center justify-center gap-2 text-sm sm:text-base ${isPaidPlan ? 'bg-green-600 hover:bg-green-700' : 'btn-primary'}`}
                  data-testid="submit-download-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      {isPaidPlan ? "Redirecting to checkout..." : "Processing..."}
                    </>
                  ) : (
                    <>
                      {isPaidPlan ? (
                        <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                      ) : (
                        <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                      {buttonLabels[planType]}
                    </>
                  )}
                </Button>

                <p className="text-[10px] sm:text-xs text-zinc-500 text-center">
                  {isPaidPlan 
                    ? "You'll be redirected to Stripe for secure payment."
                    : "We'll send you the download link and quick start guide."}
                </p>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailCaptureModal;
